import {
    roundCentsUpToNearestHundredDollars,
    roundCentsUpToNearestThousandDollars,
} from '@fruitful/fruitils';
import { MoneyMapCategoryToAccountColorMap } from '@fruitful/money-map';
import {
    AccountOwnership,
    HolisticCategory,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountUpsertDto,
    MoneyMapCategory,
    MoneyMapRuleGoalType,
    type MoneyMapRuleGroupUpsertDto,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    PaycheckRoutingRuleType,
    Portfolio,
    RiskLevel,
    SupportedApexAccountRegistrationType,
    TimeHorizon,
} from '@fruitful/types';
import { FRUITFUL_ICON_KEY } from 'smoothie';

import {
    type MembershipDetails,
    MembershipType,
} from '../../entities/entityTypes';
import {
    PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
    PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
} from '../types/GoalPresets';
import {
    getBackdoorRothPersonalizedAccountDefaults,
    getBackdoorRothPresetLabel,
} from './backdoorRothFormShared';
import { getAccountOwnershipAndPrimaryUser } from './moneyMapUtils';

export enum GoalPanelPreset {
    NEW_GOAL = 'NEW_GOAL',
    ONE_MONTH_EMERGENCY_FUND = 'ONE_MONTH_EMERGENCY_FUND',
    FULL_EMERGENCY_FUND = 'FULL_EMERGENCY_FUND',
    FINANCIAL_HEALTH = 'FINANCIAL_HEALTH',
    INVESTMENT_FUND = 'INVESTMENT_FUND',
    BACKDOOR_ROTH = 'BACKDOOR_ROTH',
    // Backdoor Roth targeting the partner (second member) on a joint membership.
    BACKDOOR_ROTH_PARTNER = 'BACKDOOR_ROTH_PARTNER',
    PAY_OFF_DEBT = 'PAY_OFF_DEBT',
    DOWN_PAYMENT = 'DOWN_PAYMENT',
    EDUCATION_FUND = 'EDUCATION_FUND',
    BUY_A_CAR = 'BUY_A_CAR',
    TRAVEL_FUND = 'TRAVEL_FUND',
    GIFTS_FUND = 'GIFTS_FUND',
}

export const GoalPanelPresetLabel: Record<GoalPanelPreset, string> = {
    [GoalPanelPreset.NEW_GOAL]: 'New goal',
    [GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND]: '1 month emergency fund',
    [GoalPanelPreset.FULL_EMERGENCY_FUND]: 'Full emergency fund',
    [GoalPanelPreset.FINANCIAL_HEALTH]: 'Financial health',
    [GoalPanelPreset.INVESTMENT_FUND]: 'Investment fund',
    [GoalPanelPreset.BACKDOOR_ROTH]: 'Backdoor Roth',
    [GoalPanelPreset.BACKDOOR_ROTH_PARTNER]: 'Backdoor Roth',
    [GoalPanelPreset.PAY_OFF_DEBT]: 'Pay off debt',
    [GoalPanelPreset.DOWN_PAYMENT]: 'Down payment',
    [GoalPanelPreset.EDUCATION_FUND]: 'Education fund',
    [GoalPanelPreset.BUY_A_CAR]: 'Buy a car',
    [GoalPanelPreset.TRAVEL_FUND]: 'Travel fund',
    [GoalPanelPreset.GIFTS_FUND]: 'Gifts fund',
};

/** Multi-goal presets use edge-only drop zones (no center merge), like multi-goal groups. */
export const isSingleGoalPanelPreset = (preset: GoalPanelPreset): boolean =>
    preset !== GoalPanelPreset.FINANCIAL_HEALTH;

/**
 * True when a joint membership has a partner (second member) — the point at which the
 * partner Backdoor Roth preset becomes available.
 */
export const isJointMembershipWithPartner = (
    membership: MembershipDetails | null | undefined,
): boolean =>
    membership?.membershipType === MembershipType.JOINT &&
    (membership?.users?.length ?? 0) > 1;

/**
 * Preset tile/label text. Backdoor Roth presets are personalized with the member's first name
 * on joint memberships (e.g. `Bob's Backdoor Roth` / `Jane's Backdoor Roth`) so the two tiles
 * can be told apart; solo memberships keep the plain `Backdoor Roth` label.
 */
export const getGoalPanelPresetLabel = (
    preset: GoalPanelPreset,
    membership: MembershipDetails | null | undefined,
): string => {
    if (preset === GoalPanelPreset.BACKDOOR_ROTH) {
        return isJointMembershipWithPartner(membership)
            ? getBackdoorRothPresetLabel(membership, 0)
            : GoalPanelPresetLabel[GoalPanelPreset.BACKDOOR_ROTH];
    }
    if (preset === GoalPanelPreset.BACKDOOR_ROTH_PARTNER) {
        return getBackdoorRothPresetLabel(membership, 1);
    }
    return GoalPanelPresetLabel[preset];
};

export const getGoalPanelPresetRuleCount = (preset: GoalPanelPreset): number =>
    isSingleGoalPanelPreset(preset) ? 1 : 2;

export const GoalPanelPresetDescription: Record<GoalPanelPreset, string> = {
    [GoalPanelPreset.NEW_GOAL]: 'Insert a new goal.',
    [GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND]:
        'Covers 1 month of expenses when emergencies hit.',
    [GoalPanelPreset.FULL_EMERGENCY_FUND]: 'Builds on your emergency fund.',
    [GoalPanelPreset.FINANCIAL_HEALTH]:
        'Gets you started with funding debt, emergency fund, and retirement goals.',
    [GoalPanelPreset.INVESTMENT_FUND]: 'Investment and retirement funds.',
    [GoalPanelPreset.BACKDOOR_ROTH]:
        'Contribute to a Traditional IRA, then convert to a Roth IRA.',
    [GoalPanelPreset.BACKDOOR_ROTH_PARTNER]:
        'Contribute to a Traditional IRA, then convert to a Roth IRA.',
    [GoalPanelPreset.PAY_OFF_DEBT]: 'Pay off your high interest debt',
    [GoalPanelPreset.DOWN_PAYMENT]:
        'Save for a down payment on your first home.',
    [GoalPanelPreset.EDUCATION_FUND]: "Save for your kid's education.",
    [GoalPanelPreset.BUY_A_CAR]: 'Buy a car.',
    [GoalPanelPreset.TRAVEL_FUND]: 'Save to travel every year.',
    [GoalPanelPreset.GIFTS_FUND]: 'Save for gifts.',
};

const GOALS_ACCOUNT_COLOR = MoneyMapCategoryToAccountColorMap[
    MoneyMapCategory.GOALS
] as string;

const buildCashAccountDto = (
    membership: MembershipDetails,
    name: string,
    category: HolisticCategory = HolisticCategory.SAVE,
): MoneyMapAccountUpsertDto => {
    const { ownership, primaryUserId } =
        getAccountOwnershipAndPrimaryUser(membership);
    return {
        membershipId: membership.id,
        type: MoneyMapAccountType.INTERNAL_BANKING,
        status: MoneyMapAccountStatus.PROPOSED,
        createParameters: {
            name,
            ownership,
            primaryUserId,
            category,
            virtualCard: false,
            physicalCard: false,
            icon: FRUITFUL_ICON_KEY,
            color: GOALS_ACCOUNT_COLOR,
        },
    };
};

const buildApexAccountDto = (
    membership: MembershipDetails,
    name: string,
    primaryUserId: string,
): MoneyMapAccountUpsertDto => ({
    membershipId: membership.id,
    type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
    status: MoneyMapAccountStatus.PROPOSED,
    createParameters: {
        name,
        ownership: AccountOwnership.INDIVIDUAL,
        primaryUserId,
        apexAccountType:
            SupportedApexAccountRegistrationType.RegistrationIraRoth,
        apexPortfolio: Portfolio.CORE,
        apexRiskLevel: RiskLevel.TEN,
        apexTimeHorizon: TimeHorizon.AVERAGE,
        icon: FRUITFUL_ICON_KEY,
        color: GOALS_ACCOUNT_COLOR,
    },
});

const buildRetirementRules = (
    membership: MembershipDetails,
    moneyMapId: string,
    priority: number,
): MoneyMapRuleUpsertDto[] => {
    const isJoint =
        membership.membershipType === MembershipType.JOINT &&
        membership.users.length > 1;

    if (isJoint) {
        return membership.users.map(({ firstName, id }) => {
            const description = `${firstName} Retirement`;
            return {
                moneyMapId,
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description,
                priority,
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 0.02,
                moneyMapAccount: buildApexAccountDto(
                    membership,
                    description,
                    id,
                ),
            };
        });
    }

    return [
        {
            moneyMapId,
            type: MoneyMapRuleType.GOAL,
            goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
            description: 'Retirement',
            priority,
            routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
            percentage: 0.05,
            moneyMapAccount: buildApexAccountDto(
                membership,
                'Retirement',
                membership.users[0]?.id as string,
            ),
        },
    ];
};

export const buildGoalPanelPresetGroupDto = ({
    preset,
    membership,
    moneyMapId,
    priority,
    monthlyExpensesCents,
}: {
    preset: GoalPanelPreset;
    membership: MembershipDetails;
    moneyMapId: string;
    priority: number;
    monthlyExpensesCents: number;
}): MoneyMapRuleGroupUpsertDto => {
    const isJoint =
        membership.membershipType === MembershipType.JOINT &&
        membership.users.length > 1;

    const oneMonthTarget =
        roundCentsUpToNearestHundredDollars(monthlyExpensesCents);
    const threeMonthTarget = roundCentsUpToNearestThousandDollars(
        monthlyExpensesCents * 3,
    );

    const singleGoalGroup = (
        name: string,
        rule: Omit<MoneyMapRuleUpsertDto, 'moneyMapId' | 'priority'>,
    ): MoneyMapRuleGroupUpsertDto => ({
        moneyMapId,
        priority,
        percentage: 1,
        name,
        moneyMapRules: [
            {
                ...rule,
                moneyMapId,
                priority,
            },
        ],
    });

    // `userIndex` selects the owning member (0 = primary, 1 = partner on a joint membership).
    const buildBackdoorRothGroup = (
        userIndex: number,
    ): MoneyMapRuleGroupUpsertDto => {
        const personalized = getBackdoorRothPersonalizedAccountDefaults(
            membership,
            userIndex,
        );
        const tradName = personalized?.tradAccountName ?? 'Trad IRA';
        const rothName = personalized?.rothAccountName ?? 'Backdoor Roth IRA';
        const primaryUserId =
            personalized?.primaryUserId ??
            (membership.users[userIndex]?.id as string);

        return singleGoalGroup('Backdoor Roth', {
            type: MoneyMapRuleType.GOAL,
            goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
            description: 'Backdoor Roth',
            routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
            percentage: 0.05,
            // Per the API contract: rule's primary money-map account is the Trad IRA;
            // the paired Roth IRA is nested under `backdoorRothLinkedMoneyMapAccount`.
            moneyMapAccount: {
                membershipId: membership.id,
                type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
                status: MoneyMapAccountStatus.PROPOSED,
                createParameters: {
                    name: tradName,
                    ownership: AccountOwnership.INDIVIDUAL,
                    primaryUserId,
                    apexAccountType:
                        SupportedApexAccountRegistrationType.RegistrationIraTraditional,
                    icon: FRUITFUL_ICON_KEY,
                    color: GOALS_ACCOUNT_COLOR,
                    backdoorRothRole: 'TRADITIONAL',
                },
                backdoorRothLinkedMoneyMapAccount: {
                    membershipId: membership.id,
                    type: MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT,
                    status: MoneyMapAccountStatus.PROPOSED,
                    createParameters: {
                        name: rothName,
                        ownership: AccountOwnership.INDIVIDUAL,
                        primaryUserId,
                        apexAccountType:
                            SupportedApexAccountRegistrationType.RegistrationIraRoth,
                        apexPortfolio: Portfolio.CORE,
                        apexRiskLevel: RiskLevel.TEN,
                        apexTimeHorizon: TimeHorizon.AVERAGE,
                        icon: FRUITFUL_ICON_KEY,
                        color: GOALS_ACCOUNT_COLOR,
                        backdoorRothRole: 'ROTH',
                    },
                },
            },
        });
    };

    switch (preset) {
        case GoalPanelPreset.NEW_GOAL:
            return singleGoalGroup('New goal', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description: 'New goal',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'New goal',
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND:
            return singleGoalGroup('1 Month Emergency Fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                description: '1 Month Emergency Fund',
                routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                percentage: 1,
                targetBalanceCents: oneMonthTarget,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.FULL_EMERGENCY_FUND:
            return singleGoalGroup('Full Emergency Fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                description: 'Full Emergency Fund',
                routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                percentage: 1,
                targetBalanceCents: threeMonthTarget,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.FINANCIAL_HEALTH:
            return {
                moneyMapId,
                priority,
                percentage: 1,
                name: 'Financial Health',
                moneyMapRules: [
                    {
                        moneyMapId,
                        type: MoneyMapRuleType.GOAL,
                        goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                        description: 'Full Emergency Fund',
                        priority,
                        routingRuleType:
                            PaycheckRoutingRuleType.MINIMUM_BALANCE,
                        percentage: isJoint ? 0.16 : 0.15,
                        targetBalanceCents: threeMonthTarget,
                        moneyMapAccount: buildCashAccountDto(
                            membership,
                            PRESET_EMERGENCY_FUND_ACCOUNT_NAME,
                            HolisticCategory.SAVE,
                        ),
                    },
                    {
                        moneyMapId,
                        type: MoneyMapRuleType.GOAL,
                        goalType: MoneyMapRuleGoalType.DEBT_PAY_DOWN,
                        description: PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                        priority,
                        routingRuleType:
                            PaycheckRoutingRuleType.MINIMUM_BALANCE,
                        percentage: 0.8,
                        targetBalanceCents: null,
                        moneyMapAccount: buildCashAccountDto(
                            membership,
                            PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                            HolisticCategory.SAVE,
                        ),
                    },
                    ...buildRetirementRules(membership, moneyMapId, priority),
                ],
            };

        case GoalPanelPreset.INVESTMENT_FUND:
            return singleGoalGroup('Investment Fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description: 'Investment fund',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 0.05,
                moneyMapAccount: buildApexAccountDto(
                    membership,
                    'Investment fund',
                    membership.users[0]?.id as string,
                ),
            });

        case GoalPanelPreset.BACKDOOR_ROTH:
            return buildBackdoorRothGroup(0);

        case GoalPanelPreset.BACKDOOR_ROTH_PARTNER:
            return buildBackdoorRothGroup(1);

        case GoalPanelPreset.PAY_OFF_DEBT:
            return singleGoalGroup('Pay off debt', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.DEBT_PAY_DOWN,
                description: 'Pay off debt',
                routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                percentage: 1,
                targetBalanceCents: null,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    PRESET_DEBT_PAY_DOWN_ACCOUNT_NAME,
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.DOWN_PAYMENT:
            return singleGoalGroup('Down payment', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                description: 'Down payment',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'House savings',
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.EDUCATION_FUND:
            return singleGoalGroup('Education fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description: 'Education fund',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'Education fund',
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.BUY_A_CAR:
            return singleGoalGroup('Buy a car', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE,
                description: 'Buy a car',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'Car savings',
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.TRAVEL_FUND:
            return singleGoalGroup('Travel fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description: 'Travel fund',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'Travel',
                    HolisticCategory.SAVE,
                ),
            });

        case GoalPanelPreset.GIFTS_FUND:
            return singleGoalGroup('Gifts fund', {
                type: MoneyMapRuleType.GOAL,
                goalType: MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
                description: 'Gifts fund',
                routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
                percentage: 1,
                moneyMapAccount: buildCashAccountDto(
                    membership,
                    'Gifts',
                    HolisticCategory.SAVE,
                ),
            });
    }
};
